import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ExternalLink, FileText } from "lucide-react";
import { adminFetch } from "./adminApi";
import RichTextEditor from "./RichTextEditor";

const CATEGORIES = ["Banking", "SSC", "Railway", "MBA", "Insurance", "Career", "Vedanti"];

const EMPTY = {
  title: "", slug: "", category: "Banking", excerpt: "", content: "",
  author: "Vedanti Academy", read_time: "5 min", date: "", is_published: true,
};

export default function AdminBlogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await adminFetch("/admin/blogs"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (b) => { setForm({ ...EMPTY, ...b }); setEditing(b); };
  const close = () => setEditing(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const plainContent = form.content.replace(/<[^>]*>/g, "").trim();
    if (!form.title || !form.excerpt || !plainContent) { toast.error("Title, Excerpt and Content are required"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await adminFetch(`/admin/blogs/${editing.id}`, { method: "PUT", body: JSON.stringify(form) });
        toast.success("Blog updated");
      } else {
        await adminFetch("/admin/blogs", { method: "POST", body: JSON.stringify(form) });
        toast.success("Blog created");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try {
      await adminFetch(`/admin/blogs/${b.id}`, { method: "DELETE" });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="admin-blogs-page">
      <Helmet><title>Manage Blogs | Vedanti Admin</title></Helmet>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-vedanti-navy flex items-center gap-2">
            <FileText size={22} className="text-vedanti-orange" /> Blog Posts
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">{items.length} custom posts · appear under /resources alongside library articles</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-vedanti-orange hover:bg-orange-600 text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-all" data-testid="add-blog-btn">
          <Plus size={16} /> Add Blog
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400">
          No custom blogs yet. Click &quot;Add Blog&quot; to publish your first article.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Category</th>
                <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50" data-testid={`admin-blog-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-vedanti-navy line-clamp-1">{b.title}</p>
                    <a href={`/resources/${b.slug}`} target="_blank" rel="noopener noreferrer nofollow" className="text-[11px] text-vedanti-orange inline-flex items-center gap-1">
                      /resources/{b.slug} <ExternalLink size={10} />
                    </a>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs font-bold text-slate-500">{b.category}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-500">{b.date}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${b.is_published ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
                      {b.is_published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(b)} className="text-slate-500 hover:text-vedanti-navy p-1.5" data-testid={`edit-blog-${b.id}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(b)} className="text-slate-400 hover:text-red-500 p-1.5" data-testid={`delete-blog-${b.id}`}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="blog-form-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-heading font-bold text-vedanti-navy">{editing.id ? "Edit" : "New"} Blog Post</h2>
              <button onClick={close} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-vedanti-navy mb-1">Title *</label>
                <input value={form.title} onChange={set("title")} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40" data-testid="blog-field-title" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-vedanti-navy mb-1">Category</label>
                  <select value={form.category} onChange={set("category")} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40" data-testid="blog-field-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-vedanti-navy mb-1">Read Time</label>
                  <input value={form.read_time} onChange={set("read_time")} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40" placeholder="6 min" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-vedanti-navy mb-1">Slug (auto if blank)</label>
                  <input value={form.slug} onChange={set("slug")} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40" placeholder="how-to-crack-ibps" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-vedanti-navy mb-1">Date</label>
                  <input type="date" value={form.date} onChange={set("date")} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-vedanti-navy mb-1">Excerpt *</label>
                <textarea value={form.excerpt} onChange={set("excerpt")} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm min-h-[70px] focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40" data-testid="blog-field-excerpt" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-vedanti-navy mb-1">Content *</label>
                <RichTextEditor
                  key={editing.id || "new"}
                  value={form.content}
                  onChange={(html) => setForm((f) => ({ ...f, content: html }))}
                  placeholder="Write your article — use the toolbar for headings, bold, lists, links, images and more…"
                />
                <p className="text-[11px] text-slate-400 mt-1">Rich text editor — formatting, headings, lists, quotes, links &amp; images. Content is sanitized before it appears on the website.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-vedanti-navy mb-1">Author</label>
                  <input value={form.author} onChange={set("author")} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40" />
                </div>
                <label className="flex items-end gap-2 text-sm font-semibold text-vedanti-navy pb-2">
                  <input type="checkbox" checked={form.is_published} onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))} className="w-4 h-4 accent-vedanti-orange" data-testid="blog-field-published" />
                  Published
                </label>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex justify-end gap-3">
              <button onClick={close} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button onClick={save} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-bold bg-vedanti-orange hover:bg-orange-600 text-white disabled:opacity-60" data-testid="blog-save-btn">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
